import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  MapPin, 
  User, 
  Phone, 
  Car, 
  Clock, 
  Copy, 
  Share2, 
  Calendar, 
  Shield, 
  Headphones,
  Mail,
  Award
} from 'lucide-react';

const BookingConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingDetails } = location.state || {};
  const [bookingRef, setBookingRef] = useState('');
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (bookingDetails?.bookingId) {
      setBookingRef(bookingDetails.bookingId);
    } else {
      setBookingRef(`STC${Math.floor(Math.random() * 9000000) + 1000000}`);
    }
    setTimeout(() => setVisible(true), 80);
  }, [bookingDetails]);

  const copyBookingRef = () => {
    navigator.clipboard?.writeText(bookingRef).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!bookingDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-md text-center border border-gray-100">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🔍</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Booking Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find your booking details</p>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg shadow-blue-200"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .bc-root {
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh;
          background: #0b0f1a;
          color: #e8eaf0;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 32px 16px 64px;
          position: relative;
          overflow-x: hidden;
        }

        .bc-root::before {
          content: '';
          position: fixed;
          top: -20%;
          left: 50%;
          transform: translateX(-50%);
          width: 900px;
          height: 500px;
          background: radial-gradient(ellipse, rgba(0,212,130,0.08) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }

        .bc-root::after {
          content: '';
          position: fixed;
          bottom: -10%;
          right: -10%;
          width: 600px;
          height: 600px;
          background: radial-gradient(ellipse, rgba(59,130,246,0.06) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }

        .bc-container {
          width: 100%;
          max-width: 480px;
          position: relative;
          z-index: 1;
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.55s cubic-bezier(0.22,1,0.36,1), transform 0.55s cubic-bezier(0.22,1,0.36,1);
        }
        .bc-container.visible { opacity: 1; transform: translateY(0); }

        .bc-header {
          text-align: center;
          margin-bottom: 28px;
          padding-top: 8px;
        }

        .bc-check-ring {
          width: 80px; height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #00d482 0%, #00b36b 100%);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 20px;
          box-shadow: 0 0 0 12px rgba(0,212,130,0.1), 0 0 0 24px rgba(0,212,130,0.05);
          animation: ringPulse 2.4s ease-in-out infinite;
        }
        @keyframes ringPulse {
          0%, 100% { box-shadow: 0 0 0 12px rgba(0,212,130,0.1), 0 0 0 24px rgba(0,212,130,0.05); }
          50%       { box-shadow: 0 0 0 16px rgba(0,212,130,0.14), 0 0 0 32px rgba(0,212,130,0.06); }
        }

        .bc-title {
          font-family: 'Syne', sans-serif;
          font-size: 28px;
          font-weight: 800;
          letter-spacing: -0.5px;
          color: #ffffff;
          line-height: 1.15;
        }
        .bc-subtitle {
          font-size: 14px;
          color: #6b7280;
          margin-top: 6px;
          font-weight: 400;
        }

        .bc-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px;
          margin-bottom: 12px;
          overflow: hidden;
          backdrop-filter: blur(12px);
        }

        .bc-card-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 20px 14px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .bc-card-icon {
          width: 32px; height: 32px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .bc-card-icon.green  { background: rgba(0,212,130,0.12); color: #00d482; }
        .bc-card-icon.blue   { background: rgba(59,130,246,0.12); color: #60a5fa; }
        .bc-card-icon.amber  { background: rgba(251,191,36,0.12); color: #fbbf24; }
        .bc-card-icon.violet { background: rgba(167,139,250,0.12); color: #a78bfa; }

        .bc-card-title {
          font-family: 'Syne', sans-serif;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: #9ca3af;
        }

        .bc-card-body { padding: 16px 20px 20px; }

        .bc-ref-card {
          background: linear-gradient(135deg, rgba(0,212,130,0.08) 0%, rgba(0,179,107,0.04) 100%);
          border: 1px solid rgba(0,212,130,0.2);
          border-radius: 20px;
          padding: 20px 24px;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .bc-ref-label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: #00d482;
          margin-bottom: 4px;
        }
        .bc-ref-value {
          font-family: 'Syne', sans-serif;
          font-size: 22px;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: 1px;
        }
        .bc-copy-btn {
          flex-shrink: 0;
          background: rgba(0,212,130,0.12);
          border: 1px solid rgba(0,212,130,0.25);
          color: #00d482;
          padding: 10px 16px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex; align-items: center; gap: 6px;
          font-family: 'DM Sans', sans-serif;
        }
        .bc-copy-btn:hover { background: rgba(0,212,130,0.2); }
        .bc-copy-btn.copied { background: rgba(0,212,130,0.2); color: #00d482; }

        .bc-row {
          display: flex;
          gap: 16px;
          margin-top: 12px;
        }
        .bc-field { flex: 1; }
        .bc-field-label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: #4b5563;
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .bc-field-value {
          font-size: 14px;
          font-weight: 500;
          color: #e2e8f0;
          line-height: 1.4;
        }

        .bc-route {
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        .bc-route-stop {
          display: flex;
          gap: 14px;
          align-items: flex-start;
        }
        .bc-route-dot-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding-top: 4px;
          flex-shrink: 0;
        }
        .bc-dot {
          width: 10px; height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .bc-dot.green { background: #00d482; box-shadow: 0 0 8px rgba(0,212,130,0.6); }
        .bc-dot.red   { background: #f87171; box-shadow: 0 0 8px rgba(248,113,113,0.5); }
        .bc-route-line {
          width: 1px;
          flex: 1;
          min-height: 24px;
          background: linear-gradient(to bottom, rgba(0,212,130,0.3), rgba(248,113,113,0.3));
          margin: 4px 0;
        }
        .bc-stop-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: #4b5563;
          margin-bottom: 2px;
        }
        .bc-stop-value {
          font-size: 13.5px;
          font-weight: 500;
          color: #cbd5e1;
          line-height: 1.4;
          padding-bottom: 16px;
        }

        .bc-vehicle-pill {
          display: flex; align-items: center; gap: 10px;
          background: rgba(251,191,36,0.06);
          border: 1px solid rgba(251,191,36,0.12);
          border-radius: 12px;
          padding: 12px 14px;
          margin-top: 4px;
        }
        .bc-vehicle-icon { color: #fbbf24; }
        .bc-vehicle-name { font-size: 15px; font-weight: 600; color: #e2e8f0; }
        .bc-vehicle-type { font-size: 12px; color: #6b7280; margin-top: 1px; }

        .bc-package-badge {
          background: rgba(167,139,250,0.1);
          border: 1px solid rgba(167,139,250,0.2);
          border-radius: 20px;
          padding: 4px 12px;
          font-size: 12px;
          color: #a78bfa;
          display: inline-block;
          margin-top: 8px;
        }

        .bc-divider { height: 1px; background: rgba(255,255,255,0.05); margin: 12px 0; }

        .bc-actions {
          display: flex;
          gap: 10px;
          margin-top: 12px;
        }
        .bc-action-btn {
          flex: 1;
          padding: 14px 12px;
          border-radius: 14px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          display: flex; align-items: center; justify-content: center; gap: 7px;
          transition: all 0.2s;
          font-family: 'DM Sans', sans-serif;
        }
        .bc-action-btn.ghost {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          color: #9ca3af;
        }
        .bc-action-btn.ghost:hover { background: rgba(255,255,255,0.08); color: #e2e8f0; }
        .bc-action-btn.primary {
          background: linear-gradient(135deg, #00d482 0%, #00a86b 100%);
          color: #0b1a10;
          box-shadow: 0 4px 20px rgba(0,212,130,0.25);
        }
        .bc-action-btn.primary:hover { box-shadow: 0 6px 28px rgba(0,212,130,0.35); transform: translateY(-1px); }
        .bc-action-btn.secondary {
          background: rgba(59,130,246,0.1);
          border: 1px solid rgba(59,130,246,0.2);
          color: #60a5fa;
        }
        .bc-action-btn.secondary:hover { background: rgba(59,130,246,0.18); }

        .bc-support {
          background: rgba(59,130,246,0.06);
          border: 1px solid rgba(59,130,246,0.12);
          border-radius: 20px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 12px;
        }
        .bc-support-icon {
          width: 48px; height: 48px; flex-shrink: 0;
          border-radius: 14px;
          background: rgba(59,130,246,0.12);
          display: flex; align-items: center; justify-content: center;
          color: #60a5fa;
        }
        .bc-support-text { flex: 1; }
        .bc-support-title { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; color: #ffffff; }
        .bc-support-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
        .bc-support-btns { display: flex; gap: 8px; flex-shrink: 0; }
        .bc-support-btn {
          padding: 8px 14px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.2s;
        }
        .bc-support-btn.call { background: rgba(59,130,246,0.15); border: 1px solid rgba(59,130,246,0.25); color: #60a5fa; }
        .bc-support-btn.call:hover { background: rgba(59,130,246,0.25); }
        .bc-support-btn.wa { background: rgba(0,212,130,0.12); border: 1px solid rgba(0,212,130,0.22); color: #00d482; }
        .bc-support-btn.wa:hover { background: rgba(0,212,130,0.2); }

        .bc-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.4px;
        }
        .bc-badge.green { background: rgba(0,212,130,0.12); color: #00d482; border: 1px solid rgba(0,212,130,0.2); }

        .bc-footer-note {
          text-align: center;
          font-size: 12.5px;
          color: #4b5563;
          margin-top: 8px;
          padding: 0 8px;
          line-height: 1.5;
        }
        .bc-footer-note span { color: #00d482; font-weight: 500; }

        .bc-fare-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .bc-fare-item:last-child { border-bottom: none; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .bc-container.visible .bc-header      { animation: fadeUp 0.5s 0.05s both; }
        .bc-container.visible .bc-ref-card    { animation: fadeUp 0.5s 0.15s both; }
        .bc-container.visible .bc-card:nth-child(3) { animation: fadeUp 0.5s 0.22s both; }
        .bc-container.visible .bc-card:nth-child(4) { animation: fadeUp 0.5s 0.28s both; }
        .bc-container.visible .bc-card:nth-child(5) { animation: fadeUp 0.5s 0.34s both; }
        .bc-container.visible .bc-support     { animation: fadeUp 0.5s 0.40s both; }
        .bc-container.visible .bc-actions     { animation: fadeUp 0.5s 0.46s both; }
        .bc-container.visible .bc-footer-note { animation: fadeUp 0.5s 0.52s both; }
      `}</style>

      <div className="bc-root">
        <div className={`bc-container ${visible ? 'visible' : ''}`}>

          {/* Header */}
          <div className="bc-header">
            <div className="bc-check-ring">
              <CheckCircle size={38} strokeWidth={2.5} color="#0b1a10" />
            </div>
            <div className="bc-title">Booking Confirmed!</div>
            <div className="bc-subtitle">Your {bookingDetails.bookingType} request has been received</div>
          </div>

          {/* Booking Reference */}
          {/* <div className="bc-ref-card">
            <div>
              <div className="bc-ref-label">Booking Reference</div>
              <div className="bc-ref-value">{bookingRef}</div>
            </div>
            <button className={`bc-copy-btn ${copied ? 'copied' : ''}`} onClick={copyBookingRef}>
              <Copy size={13} strokeWidth={2.5} />
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div> */}

          {/* Customer Details */}
          <div className="bc-card">
            <div className="bc-card-header">
              <div className="bc-card-icon green"><User size={15} strokeWidth={2.5} /></div>
              <span className="bc-card-title">Customer Details</span>
              <span className="bc-badge green" style={{marginLeft:'auto'}}><Shield size={10}/> Verified</span>
            </div>
            <div className="bc-card-body">
              <div className="bc-row" style={{marginTop:0}}>
                <div className="bc-field">
                  <div className="bc-field-label">Full Name</div>
                  <div className="bc-field-value">{bookingDetails.customerName}</div>
                </div>
                <div className="bc-field">
                  <div className="bc-field-label">Mobile</div>
                  <div className="bc-field-value">+91 {bookingDetails.mobile}</div>
                </div>
              </div>
              {bookingDetails.email && bookingDetails.email !== 'Not provided' && (
                <div className="bc-row" style={{marginTop:8}}>
                  <div className="bc-field">
                    <div className="bc-field-label"><Mail size={10} style={{display:'inline',marginRight:3}} /> Email</div>
                    <div className="bc-field-value">{bookingDetails.email}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Trip Details */}
          <div className="bc-card">
            <div className="bc-card-header">
              <div className="bc-card-icon blue"><MapPin size={15} strokeWidth={2.5} /></div>
              <span className="bc-card-title">Trip Details</span>
            </div>
            <div className="bc-card-body">
              <div className="bc-route">
                <div className="bc-route-stop">
                  <div className="bc-route-dot-col">
                    <div className="bc-dot green"></div>
                    <div className="bc-route-line"></div>
                  </div>
                  <div>
                    <div className="bc-stop-label">Pickup</div>
                    <div className="bc-stop-value">{bookingDetails.pickupPoint}</div>
                  </div>
                </div>
                <div className="bc-route-stop">
                  <div className="bc-route-dot-col">
                    <div className="bc-dot red"></div>
                  </div>
                  <div>
                    <div className="bc-stop-label">Dropoff</div>
                    <div className="bc-stop-value">{bookingDetails.dropoffPoint}</div>
                  </div>
                </div>
              </div>

              <div className="bc-divider"></div>

              <div className="bc-row" style={{marginTop:0}}>
                <div className="bc-field">
                  <div className="bc-field-label"><Calendar size={10} style={{display:'inline',marginRight:3}} />Date</div>
                  <div className="bc-field-value">{bookingDetails.pickupDate}</div>
                </div>
                <div className="bc-field">
                  <div className="bc-field-label"><Clock size={10} style={{display:'inline',marginRight:3}} />Time</div>
                  <div className="bc-field-value">{bookingDetails.pickupTime}</div>
                </div>
              </div>

              {bookingDetails.dropoffDate && (
                <div className="bc-row" style={{marginTop:8}}>
                  <div className="bc-field">
                    <div className="bc-field-label">Return Date</div>
                    <div className="bc-field-value">{bookingDetails.dropoffDate}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Vehicle Details */}
          {/* <div className="bc-card">
            <div className="bc-card-header">
              <div className="bc-card-icon amber"><Car size={15} strokeWidth={2.5} /></div>
              <span className="bc-card-title">Vehicle Details</span>
            </div>
            <div className="bc-card-body">
              <div className="bc-vehicle-pill">
                <Car size={22} className="bc-vehicle-icon" />
                <div>
                  <div className="bc-vehicle-name">{bookingDetails.vehicleName}</div>
                  <div className="bc-vehicle-type">{bookingDetails.bookingType}</div>
                </div>
              </div>

              {bookingDetails.packageType && (
                <div className="bc-package-badge">
                  <Award size={12} style={{display:'inline',marginRight:4}} />
                  {bookingDetails.packageType}
                </div>
              )}

              <div className="bc-divider" style={{marginTop:14}}></div>

              <div className="bc-fare-item">
                <span className="bc-field-label">Estimated Fare</span>
                <span className="bc-field-value" style={{color:'#00d482', fontWeight:600}}>{bookingDetails.estimatedFare}</span>
              </div>
              
              <div className="bc-fare-item">
                <span className="bc-field-label">Trip Fee</span>
                <span className="bc-field-value">
                  {bookingDetails.bookingType?.toLowerCase().includes('local') ? '₹50+₹50' : '₹100+₹100'} per day
                </span>
              </div>
            </div>
          </div> */}

          {/* Quick Actions */}
          <div className="bc-actions">
            <button className="bc-action-btn ghost" onClick={() => navigator.share?.({ title: 'My Booking', text: `Booking Ref: ${bookingRef}` })}>
              <Share2 size={14} /> Share
            </button>
            {/* <button className="bc-action-btn secondary" onClick={() => navigate('/my-bookings')}>
              My Bookings
            </button>
            <button className="bc-action-btn primary" onClick={() => navigate('/track-booking', { state: { bookingRef } })}>
              Track Ride
            </button> */}
          </div>

          {/* Support */}
          <div className="bc-support">
            <div className="bc-support-icon">
              <Headphones size={22} />
            </div>
            <div className="bc-support-text">
              <div className="bc-support-title">24/7 Support</div>
              <div className="bc-support-sub">We're here to help anytime</div>
            </div>
            <div className="bc-support-btns">
              <a href="tel:+919150410309" className="bc-support-btn call">Call</a>
              <a href="https://wa.me/919150410309" target="_blank" rel="noopener noreferrer" className="bc-support-btn wa">WA</a>
            </div>
          </div>

          {/* Footer */}
          <div className="bc-footer-note">
            Our team will contact you within <span>15 minutes</span> to confirm your ride details.
          </div>

        </div>
      </div>
    </>
  );
};

export default BookingConfirmation;
